import clsx from 'clsx';
import { useMemo } from 'react';
import {
	BsVolumeMuteFill,
	BsVolumeUpFill,
	BsXLg,
	BsQuestionCircle,
} from 'react-icons/bs';

import './BayDrawer.scss';

import BayBadge from '../BayBadge';
import BayFloatManagerBase from '../BayFloatManager';
import BayTooltip from '../BayTooltip';

// BayFloatManager is still a `.jsx` module, so TS infers its defaultless props as
// required and flags every call that omits one. Loosen it here -- the real prop
// contract lives in that component.
const BayFloatManager = BayFloatManagerBase as React.ComponentType<any>;

// rc-drawer's `placement` vocabulary -> the engine's. The engine is
// writing-mode aware (`start`/`end`), rc-drawer was not; BayDrawer keeps the old
// names because 5 call sites across 3 apps pass them.
const PLACEMENT: Record<string, string> = {
	left: 'start',
	right: 'end',
	top: 'top',
	bottom: 'bottom',
};

// Which banner state a drawer has to clear is the CONSUMING APP's chrome, so the
// pixel values live in BayDrawer.scss as `--bay-float-top` and this only names
// the state. It used to be a switch returning 0/45/100/140 in this file.
const BANNER_CLASS: Record<string, string> = {
	none: 'none',
	single: 'single',
	double: 'double',
};

const HEADER_SETTINGS_PADDING = 'py-4 px-4 px-sm-5';
// Social's settings header is shorter than client's; pt-4/pb-3 is the closest
// this repo's 14px-root spacing scale gets. Unverified against a live render —
// re-check before trusting this pixel-for-pixel.
const HEADER_SETTINGS_PADDING_SOCIAL = 'pt-4 pb-3 px-4 px-sm-5';
const HEADER_DEFAULT_PADDING = 'p-2 px-sm-3';

// menuTypes that render their own header layout instead of the default
// title + close pair.
const CUSTOM_HEADER = ['links', 'launchpad', 'auto-detect-drawer'];

const BORDERED_HEADER = [
	'links',
	'launchpad',
	'recipient-drawer',
	'auto-detect-drawer',
	'settings',
];

interface BayDrawerProps {
	/**
	 * Replace the menu with your own elements
	 */
	children?: React.ReactNode;
	/**
	 * Add a title to the menu header
	 */
	title?: React.ReactNode;
	/**
	 * Function fired there is a new alert
	 */
	alertFunc?: () => void;
	/**
	 * Function fired when the drawer closes
	 */
	onCloseFunc: () => void;
	/**
	 * Play the alert sound
	 */
	alertAudio?: string | boolean;
	/**
	 * Which platform/product are we on
	 */
	menuType?: string;
	/**
	 * Collapse the menu
	 */
	openBool?: boolean;
	/**
	 * Adjust for top banners
	 */
	bannerType?: 'none' | 'single' | 'double';
	/**
	 * Placement of the drawer
	 */
	placement?: 'left' | 'top' | 'right' | 'bottom';
	/**
	 * Show footer section
	 */
	showFooter?: boolean;
	/**
	 * Footer content node
	 */
	footerContent?: React.ReactNode;
	/**
	 * Footer className
	 */
	footerClassName?: string;
	/**
	 * Set the width of the drawer dynamically
	 */
	dynamicWidth?: number | string;
	/**
	 * ID for the drawer element
	 */
	id?: string;
	/**
	 * Launchpad configuration flag
	 */
	launchpadConfig?: boolean;
	/**
	 * Additional className
	 */
	className?: string;
	/**
	 * className for the wrapper div around `children`, inside the body. Defaults
	 * to `''` when omitted.
	 */
	contentClassName?: string;
	/**
	 * Badge text to display
	 */
	badgeText?: string;
	/**
	 * Size of the header title text
	 */
	headerSize?: 'sm' | 'lg';
	/**
	 * Freeze the page while the drawer is open. Defaults to `true`, unlike the
	 * engine's edge-placement default -- see the note at the call. Pass `false` in
	 * an app whose drawer genuinely covers its scroller's scrollbar.
	 */
	lockScroll?: boolean;
	/**
	 * Applies a custom z-index to the backdrop when this drawer opens over another
	 * drawer or modal, so the backdrop is not hidden behind the panel underneath.
	 * Pass a TOKEN, not an integer -- `"var(--bs-zindex-modal, 1055)"`.
	 */
	zIndexBackdrop?: number | string;
}

/**
 * A drawer. Chrome and `menuType` variants live here; placement, motion, scrim,
 * focus management and the panel box come from `BayFloatManager`.
 *
 * Was built on `rc-drawer`. The prop surface is unchanged — all 5 call sites
 * across bay-ui, client and ui-design kept working with no edits — but the
 * emitted class names are not: `.drawer-content-wrapper` is now `.bay-drawer`,
 * `.drawer-content` is `.bay-float-body`, and `.drawer-open` no longer exists
 * because the panel is only in the DOM while open. See
 * .plan/feat-overlay-consolidation/phase-4.md.
 */
const BayDrawer = ({
	children,
	title,
	alertFunc,
	onCloseFunc,
	alertAudio,
	menuType,
	openBool = false,
	bannerType,
	placement = 'right',
	showFooter,
	footerContent,
	footerClassName = '',
	dynamicWidth = 0,
	id,
	launchpadConfig = false,
	className = '',
	contentClassName,
	badgeText = '',
	headerSize = 'sm',
	lockScroll = true,
	zIndexBackdrop,
}: BayDrawerProps) => {

	const hasCustomHeader = CUSTOM_HEADER.includes(menuType);
	const hasFooter = Boolean(showFooter && footerContent);

	// The backdrop carries the inset, not the panel. rc-drawer pushed the panel's
	// `top` down and left the mask at full height, so the scrim covered the very
	// banners the offset existed to sit below.
	const backdropClassName = useMemo(
		() =>
			clsx(
				'bay-drawer-backdrop',
				launchpadConfig && 'launchpad',
				!launchpadConfig && BANNER_CLASS[bannerType],
				menuType,
				className,
			),
		[launchpadConfig, bannerType, menuType, className],
	);

	const header = (
		<div
			className={clsx(
				'bay-drawer-header',
				menuType,
				BORDERED_HEADER.includes(menuType) ? 'border-bottom drawer-header' : 'drawer-header',
				menuType === 'settings'
					? (headerSize === 'lg' ? HEADER_SETTINGS_PADDING : HEADER_SETTINGS_PADDING_SOCIAL)
					: HEADER_DEFAULT_PADDING,
			)}
		>
			{title && !hasCustomHeader ? (
				<div className="feature-wrapper">
					{/* The settings header aligns its title on the container's own padding, so
					    zero out .feature-wrapper h2's nudge and the h2 margin.
					    Figma: 1rem / weight 600. fs-6 is 1rem and fw-bold is 600 in this theme
					    ($font-weight-semibold is 500, so fw-semibold would be too light), and
					    fw-bold is needed because .feature-wrapper h2 otherwise pins it to 500. */}
					<h2
						className={`${headerSize === 'sm' ? 'fs-6' : 'fs-4'}${menuType === 'settings' ? ' fw-bold p-0 mb-0' : ''}`}
					>
						{title}
					</h2>
				</div>
			) : (
				false
			)}

			{!hasCustomHeader ? (
				<div className="close-div pe-2">
					{menuType === 'notifications' ? (
						<BayTooltip
							overlay={
								<span className="text-capitalize">{`${
									alertAudio ? 'turn off' : 'turn on'
								} new notification sound.`}</span>
							}
							placement="left"
						>
							<span>
								<button
									aria-label="Mute Alerts"
									className="btn-audio-drawer pe-2"
									type="button"
									onClick={alertFunc}
								>
									{alertAudio ? (
										<BsVolumeUpFill color="var(--bs-dark)" size={24} />
									) : (
										<BsVolumeMuteFill color="var(--bs-dark)" size={24} />
									)}
								</button>
								<div className="vr mx-1 position-relative" style={{ top: 3 }} />
							</span>
						</BayTooltip>
					) : (
						false
					)}
					{/* Rendered at every width: this was gated to hide below 576px, which
					    left the notifications, My Account and Product Updates drawers with
					    no close affordance at all — they have no footer close button. */}
					<button
						aria-label="Close"
						className="btn-close-drawer"
						type="button"
						onClick={onCloseFunc}
					>
						<BsXLg color="var(--bs-gray-700)" size={15} />
					</button>
				</div>
			) : (
				false
			)}

			{menuType === 'links' ? (
				<>
					<div className="d-flex align-items-center p-3 mx-1">
						<span className="quickLinkHeading fw-bold lh-1">{title}</span>
						<BayTooltip
							overlay={
								<span>
									Here&apos;s where you can add any link you want quick access to. If
									you&apos;re part of a brokerage, you can also access company
									links. Take advantage of Back At You added links too!
								</span>
							}
							placement="bottom"
						>
							<span
								aria-label="Quicklinks tooltip icon"
								className="btn-audio-drawer text-700 position-relative ms-2"
								style={{ top: -1 }}
							>
								<BsQuestionCircle color="var(--bs-dark)" size={16} />
							</span>
						</BayTooltip>
					</div>
					<div className="close-div text-align-center">
						<div>
							<button
								aria-label="Close"
								className="btn-close-drawer"
								type="button"
								onClick={onCloseFunc}
							>
								<BsXLg color="var(--bs-gray-700)" size={15} />
							</button>
						</div>
					</div>
				</>
			) : (
				false
			)}

			{menuType === 'launchpad' ? (
				<>
					<div className="d-flex align-items-center p-3 mx-1">
						<span className="fs-3 fw-bold lh-1">{title}</span>
					</div>
					<div className="close-div text-align-center align-self-start">
						<div>
							<button
								aria-label="Close"
								className="btn-close-drawer"
								type="button"
								onClick={onCloseFunc}
							>
								<BsXLg color="var(--bs-gray-700)" size={15} />
							</button>
						</div>
					</div>
				</>
			) : (
				false
			)}

			{menuType === 'auto-detect-drawer' ? (
				<div className="d-flex align-items-center justify-content-between w-100 px-5">
					<div className="d-flex align-items-center gap-3">
						<div className="fs-4 fw-bold">{title}</div>
						{badgeText.length ? (
							<div>
								<BayBadge bg="indigo" isLight text="indigo">
									{badgeText}
								</BayBadge>
							</div>
						) : null}
					</div>
					<div className="close-div text-align-center">
						<button
							aria-label="Close"
							className="btn-close-drawer"
							type="button"
							onClick={onCloseFunc}
						>
							<BsXLg color="var(--bs-gray-700)" size={15} />
						</button>
					</div>
				</div>
			) : (
				false
			)}
		</div>
	);

	return (
		<BayFloatManager
			aria-label={typeof title === 'string' && title ? title : 'Drawer'}
			backdropClassName={backdropClassName}
			className={clsx('bay-drawer', menuType, className)}
			// The header is composed here rather than passed as `title`, because the
			// six menuType variants differ by more than a string. It is the first
			// child of the scrolling body and `position: sticky`, which is exactly
			// what rc-drawer's `.drawer-content > DrawerHeader` was.
			closeButton={false}
			contentClassName={contentClassName}
			footer={
				hasFooter ? (
					<div className={`border-top ${footerClassName}`}>{footerContent}</div>
				) : undefined
			}
			footerClassName="bay-drawer-footer"
			id={id}
			// Drawers default to LOCKING, which reverses BayFloatManager's own default
			// for edge placements. That default rested on "a drawer is fixed against an
			// edge and physically covers the scroll container's scrollbar", which is only
			// true when the app scrolls an element INSIDE the content area -- client's
			// `#bayBody`. It is false when the app scrolls the WINDOW, because a fixed
			// element's containing block excludes the window scrollbar, so the drawer
			// stops at its inner edge and the page scrollbar stays visible beside it.
			//
			// smc scrolls the window (`web/css/compile/smc.scss` pins html and body to
			// `height: 100%`, and overflowing content propagates to the viewport), so its
			// drawers showed the page scrollbar next to the drawer's own -- the "double
			// scroll bars" report. Centred dialogs never had it because they already lock.
			//
			// Safe where it was already unnecessary: in client the window does not scroll,
			// so `body { overflow: hidden }` is a no-op and Floating UI measures a
			// scrollbar width of 0, adding no compensating padding.
			//
			// Still a prop, because the app is the only thing that knows its own scroller.
			// Pass `lockScroll={false}` to opt out.
			lockScroll={lockScroll}
			placement={PLACEMENT[placement] ?? 'end'}
			show={openBool}
			width={dynamicWidth || 360}
			onClose={onCloseFunc}
			zIndexBackdrop={zIndexBackdrop}
		>
			{header}
			{children}
		</BayFloatManager>
	);
};

export default BayDrawer;
